import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const StatIcon = ({ type }) => {
  const icons = {
    location: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>,
    channel: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></svg>,
    announce: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>,
    listeners: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  };
  return icons[type] || null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_locations: 0,
    total_channels: 0,
    active_announcements: 0,
    total_listeners: 0
  });
  const [company, setCompany] = useState(null);
  const [nowPlaying, setNowPlaying] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchDashboardData();
  }, [token, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const companyData = localStorage.getItem('company');
      if (companyData) setCompany(JSON.parse(companyData));

      const response = await fetch(`${API}/analytics/overview`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.overview || stats);
      }
    // Fetch now playing
      try {
        const npRes = await fetch(API + '/channels/now-playing', {
          headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
        });
        if (npRes.ok) {
          const npData = await npRes.json();
          setNowPlaying(npData.channels || []);
        }
      } catch(e) { console.error('Now playing error:', e); }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Locations', value: stats.total_locations, icon: 'location', gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', bg: '#f5f3ff' },
    { label: 'Radio Channels', value: stats.total_channels, icon: 'channel', gradient: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)', bg: '#eff6ff' },
    { label: 'Active Announcements', value: stats.active_announcements, icon: 'announce', gradient: 'linear-gradient(135deg, #059669 0%, #34d399 100%)', bg: '#ecfdf5' },
    { label: 'Total Listeners', value: stats.total_listeners, icon: 'listeners', gradient: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)', bg: '#fef2f2' },
  ];

  const quickActions = [
    { label: 'Add Location', desc: 'Set up a new business location', path: '/locations', color: '#7c3aed', bg: '#f5f3ff',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg> },
    { label: 'Create Channel', desc: 'Launch a new radio channel', path: '/channels', color: '#2563eb', bg: '#eff6ff',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><circle cx="12" cy="12" r="2"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></svg> },
    { label: 'New Announcement', desc: 'Record a voice announcement', path: '/announcements', color: '#059669', bg: '#ecfdf5',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg> },
    { label: 'Generate Jingle', desc: 'Create AI-powered jingles', path: '/jingles', color: '#d97706', bg: '#fffbeb',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg> },
  ];

  const steps = [
    { num: 1, title: 'Set up your first location', desc: 'Add your business location to start broadcasting', done: stats.total_locations > 0 },
    { num: 2, title: 'Create radio channels', desc: 'Set up channels with different music and content', done: stats.total_channels > 0 },
    { num: 3, title: 'Add announcements', desc: 'Create voice announcements for your customers', done: stats.active_announcements > 0 },
    { num: 4, title: 'Go public', desc: 'Share your channels on groovie.live', done: false },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid #e9d5ff', borderTopColor: '#7c3aed',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ color: '#6b7280', fontFamily: "'Outfit', sans-serif", fontSize: '14px' }}>Loading dashboard...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#111827', letterSpacing: '-0.5px', margin: 0 }}>
          Welcome back{company ? `, ${company.name}` : ''}
        </h1>
        <p style={{ fontSize: 15, color: '#6b7280', marginTop: 6 }}>
          Here's what's happening with your radio stations today
        </p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            background: '#fff',
            borderRadius: 16,
            padding: '24px 20px',
            border: '1px solid #e5e7eb',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.15s, box-shadow 0.15s',
            cursor: 'default',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>{card.label}</p>
                <p style={{ fontSize: 32, fontWeight: 800, color: '#111827', margin: 0, letterSpacing: '-1px' }}>{card.value}</p>
              </div>
              <div style={{
                background: card.gradient,
                borderRadius: 12,
                padding: 10,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <StatIcon type={card.icon} />
              </div>
            </div>
            {/* Bottom accent */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
              background: card.gradient,
            }} />
          </div>
        ))}
      </div>

      {/* Now Playing */}
      {nowPlaying.length > 0 && (
        <div style={{
          background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
          padding: 28, marginBottom: 32,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{
              width: 10, height: 10, borderRadius: "50%", background: "#ef4444",
              boxShadow: "0 0 8px rgba(239,68,68,0.6)", animation: "pulse 2s infinite",
            }} />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.3px" }}>Now Playing</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {nowPlaying.map(function(ch) {
              return (
                <div key={ch.channel_id} style={{
                  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                  borderRadius: 14, padding: "18px 20px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>{ch.channel_name}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#a855f7" }}>{ch.listeners}</span>
                    </div>
                  </div>
                  {ch.now_playing ? (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div style={{
                          width: 6, height: 6, borderRadius: "50%", background: "#34d399", flexShrink: 0,
                          boxShadow: "0 0 6px rgba(52,211,153,0.5)",
                        }} />
                        <p style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {ch.now_playing.title}
                        </p>
                      </div>
                      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: "0 0 0 14px" }}>{ch.now_playing.artist}</p>
                      {ch.now_playing.duration > 0 && (
                        <div style={{ marginTop: 10, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.1)" }}>
                          <div style={{
                            height: "100%", borderRadius: 2,
                            background: "linear-gradient(90deg, #7c3aed, #a855f7)",
                            width: Math.min(100, (ch.now_playing.elapsed / ch.now_playing.duration) * 100) + "%",
                            transition: "width 1s linear",
                          }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", margin: 0 }}>No track playing</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions + Getting Started */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Quick Actions */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          padding: 28,
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 20px', letterSpacing: '-0.3px' }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {quickActions.map(action => (
              <button key={action.label} onClick={() => navigate(action.path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px',
                  background: action.bg,
                  border: '1px solid transparent',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  textAlign: 'left',
                  width: '100%',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = action.color + '40'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ color: action.color, flexShrink: 0 }}>{action.icon}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: action.color }}>{action.label}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{action.desc}</div>
                </div>
                <svg style={{ marginLeft: 'auto', color: action.color, opacity: 0.5 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9,18 15,12 9,6"/></svg>
              </button>
            ))}
          </div>
        </div>

        {/* Getting Started */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          padding: 28,
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 20px', letterSpacing: '-0.3px' }}>Getting Started</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {steps.map(step => (
              <div key={step.num} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: step.done ? 'linear-gradient(135deg, #059669, #34d399)' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>
                  {step.done ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>
                  ) : step.num}
                </div>
                <div>
                  <h3 style={{ fontWeight: 600, fontSize: 14, color: step.done ? '#059669' : '#111827', margin: 0, textDecoration: step.done ? 'line-through' : 'none' }}>{step.title}</h3>
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: '3px 0 0' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Live on Groovie badge */}
          <div style={{
            marginTop: 24,
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#34d399',
              boxShadow: '0 0 8px rgba(52,211,153,0.6)',
              animation: 'pulse 2s infinite',
            }} />
            <div>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Your channels are live on groovie.live</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>Listeners can tune in anytime</div>
            </div>
            <a href="https://groovie.live" target="_blank" rel="noreferrer"
              style={{
                marginLeft: 'auto',
                background: 'rgba(124,58,237,0.3)',
                color: '#c4b5fd',
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                textDecoration: 'none',
                border: '1px solid rgba(124,58,237,0.3)',
              }}
            >Visit</a>
          </div>
          <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>
        </div>
      </div>
    </div>
  );
}